import { DirectionRight, PromptQuestionCircleFill } from "@/Icons";
import { ResourceCodeEnums } from "@/api/dmp_common/interface";
import React, { useEffect, useState } from "react";
import { Radio, Toast, Tooltip } from "@ad/anq-ui";
import {
  BACKEND_FORMAT,
  CAN_SELECT_TIME_LIST,
  FRONTEND_FORMAT,
  getStartEndTime,
  getSum,
} from "../common";
import { renderCentPrice } from "@/common/render";
import { queryOverviewData } from "@/api/promotion_effect_v2";
import {
  PlanGroupListItem,
  QueryOverviewDataResult,
} from "@/api/promotion_effect_v2/interface";
import { useAuth } from "@/common/hooks/use_auth";

interface GroupListItem {
  title: string;
  planGroupCntKey: keyof QueryOverviewDataResult;
  planCntKey: keyof QueryOverviewDataResult;
  dataListKey: keyof QueryOverviewDataResult;
}

const groupList: GroupListItem[] = [
  {
    title: "品牌直推",
    planGroupCntKey: "planGroupCnt",
    planCntKey: "brandPlanCnt",
    dataListKey: "planGroupList",
  },
  {
    title: "整合营销",
    planGroupCntKey: "unionActCnt",
    planCntKey: "actPlanCnt",
    dataListKey: "unionActList",
  },
];

const dataList = [
  {
    title: "花费",
    tooltip: "广告创意被用户看到所产生的费用",
    dataIndex: "spd",
    render: renderCentPrice,
  },
  {
    title: "千次曝光花费",
    tooltip: "千次曝光花费=花费/曝光量*1000，即每曝光1000次的平均花费",
    dataIndex: "cpm",
    render: renderCentPrice,
  },
  {
    title: "交易额",
    dataIndex: "ordrSttlAmt",
    tooltip:
      "交易额=直接成交金额+间接成交金额，即用户在广告的落地页点击商品后，14天内直接购买该商品的订单金额，以及通过该商品详情页等入口跳转至店铺内其他商品并购买的订单金额",
    render: renderCentPrice,
  },
];

const ActivityPromotion = () => {
  const [hasAuth] = useAuth(ResourceCodeEnums.BRAND_DATA);
  // 近几日时间范围
  const [timeRange, setTimeRange] = useState(90);
  const [data, setData] = useState<QueryOverviewDataResult>();

  useEffect(() => {
    if (hasAuth && timeRange) {
      getData();
    }
  }, [timeRange]);

  const handleJump = () => {
    window.open(
      `/${ResourceCodeEnums.ACTIVITY_PROMOTION}?menu=${ResourceCodeEnums.BRAND_DATA}`
    );
  };

  const getData = async () => {
    const res = await queryOverviewData({
      start: getStartEndTime(timeRange).startTime.format(BACKEND_FORMAT),
      end: getStartEndTime(timeRange).endTime.format(BACKEND_FORMAT),
    });

    if (res?.success) {
      setData(res.result);
    } else {
      Toast.error(res?.errorMsg || "查询投放数据失败");
    }
  };

  return (
    <div className="mt-4 rounded-lg bg-white p-6">
      <div className="title mb-6 overflow-hidden">
        <span className="text-base font-semibold leading-8">活动与推广</span>
        <div className="title-tools float-right flex">
          <div className="title-tools float-right flex">
            <span className="mr-3 leading-8 text-text-black/[.65]">
              统计时间：
              {getStartEndTime(timeRange).startTime.format(
                FRONTEND_FORMAT
              )} ~ {getStartEndTime(timeRange).endTime.format(FRONTEND_FORMAT)}
            </span>
            <Radio.Group
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              {CAN_SELECT_TIME_LIST.map((item) => (
                <Radio.Button key={item} value={item}>
                  {item === 1 ? "昨日" : `近 ${item} 日`}
                </Radio.Button>
              ))}
            </Radio.Group>
            <span
              className="dmp-link ml-4 flex h-8 cursor-pointer items-center text-text-black/[.65]"
              onClick={handleJump}
            >
              查看更多
              <DirectionRight />
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        {groupList.map((groupItem) => (
          <div className="flex-1 bg-[#FAFAFC] p-6" key={groupItem.title}>
            <div className="title mb-4 text-sm font-semibold">
              {groupItem.title}
            </div>
            <div className="flex w-full overflow-hidden border-b border-b-[rgb(235,236,242)] pb-4">
              <div className="flex-1">
                <div className="mb-2 text-xs">计划组数</div>
                <div className="text-lg font-semibold">
                  {data?.[groupItem.planGroupCntKey] || "-"}
                </div>
              </div>
              <div className="float-left flex-1">
                <div className="mb-2 text-xs">计划数</div>
                <div className="text-lg font-semibold">
                  {data?.[groupItem.planCntKey] || "-"}
                </div>
              </div>
              <div className="flex-1" />
            </div>
            <div className="mt-4 flex">
              {dataList.map((item) => {
                const valueList = (data?.[groupItem.dataListKey] ||
                  []) as PlanGroupListItem[];

                return (
                  <div className="flex-1" key={item.dataIndex}>
                    <div className="mb-2 flex items-center">
                      <span className="text-xs">{item.title}</span>
                      <Tooltip title={item.tooltip}>
                        <PromptQuestionCircleFill className="ml-1 cursor-pointer text-question" />
                      </Tooltip>
                    </div>
                    {valueList.length ? (
                      <div>
                        <span className="text-lg font-semibold">
                          {item.render(
                            getSum(
                              valueList?.map(
                                (listItem) => listItem[item.dataIndex]
                              )
                            )
                          )}
                        </span>
                        <span className="ml-1">元</span>
                      </div>
                    ) : (
                      <div className="text-lg font-semibold">-</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityPromotion;
